<template>
  <div class="panel precios-altos-panel">
    <h3>Productos con Precio Alto
      <span class="total-badge">{{ alertas.length }} productos</span>
    </h3>
    <p class="subtitulo">Productos donde tu precio supera al competidor mas barato</p>

    <div class="controls">
      <label>Mostrar diferencias mayores a:</label>
      <input type="range" min="5" max="50" v-model.number="umbralLocal" @input="emitChange" />
      <span class="umbral-value">{{ umbralLocal }}%</span>
    </div>

    <div v-if="alertasFiltradas.length > 0" class="tiendas-list">
      <div v-for="(items, tienda) in agrupadoPorTienda" :key="tienda" class="tienda-row" @click="abrirModal(tienda, items)">
        <div class="tienda-info">
          <span class="tienda-nombre">{{ formatStoreName(tienda) }}</span>
          <span class="tienda-count">{{ items.length }} productos — diff promedio {{ promedio(items) }}%</span>
        </div>
        <button class="ver-btn">Ver detalle →</button>
      </div>
    </div>
    <div v-else class="no-alerts-message">
      No se encontraron productos con diferencia mayor al {{ umbralLocal }}%.
    </div>

    <!-- Modal de detalle de producto (compartido) -->
    <ModalProducto
      v-model:visible="detalleVisible"
      :codigo="detalleCodigo"
      :descripcion="detalleDescripcion"
      :initial-vista="vista"
    />

    <!-- Modal de lista de productos por tienda -->
    <div v-if="modalVisible" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4>{{ formatStoreName(modalTienda) }} — {{ modalItems.length }} productos mas baratos</h4>
          <div class="modal-actions">
            <button @click="exportarExcel" class="export-btn"><PhChartBar :size="13" weight="bold" /> EXCEL</button>
            <button @click="exportarCsv" class="export-btn export-btn-csv"><PhDownload :size="13" weight="bold" /> CSV</button>
            <button @click="cerrarModal" class="close-btn"><PhX :size="14" weight="bold" /></button>
          </div>
        </div>
        <div class="modal-body">
          <table class="modal-table">
            <thead>
              <tr>
                <th>Codigo</th>
                <th>Producto</th>
                <th>Tu Precio</th>
                <th>Competidor</th>
                <th>Diferencia</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in modalItemsOrdenados" :key="item.codigo">
                <td class="code-cell">
                  <span class="codigo-link" @click="abrirDetalle(item.codigo, item.descripcion)">[{{ item.codigo }}]</span>
                </td>
                <td>{{ item.descripcion }}</td>
                <td class="price-alto">
                  <a v-if="item.url_visaovip" :href="item.url_visaovip" target="_blank" class="price-link-alto">${{ item.precio_cliente }}</a>
                  <span v-else>${{ item.precio_cliente }}</span>
                </td>
                <td class="price-bajo">
                  <a v-if="item.url_competidor && !item.url_competidor.includes('lista-txt')" :href="item.url_competidor" target="_blank" class="price-link-bajo">${{ item.precio_competidor }}</a>
                  <span v-else>${{ item.precio_competidor }}</span>
                </td>
                <td class="diff-cell">+{{ item.diferencia_pct }}%</td>
                <td>
                  <button v-if="esSuperadmin" class="corregir-small" @click="marcarCorreccion(item.codigo)"><PhWarning :size="13" weight="bold" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useCorreccion } from "../composables/useCorreccion.js";
import ModalProducto from './ModalProducto.vue';
import { exportarAExcel, exportarACsv } from '../composables/useExcelExport';
import { PhChartBar, PhDownload, PhX, PhWarning } from '@phosphor-icons/vue'

const { esSuperadmin, marcarCorreccion } = useCorreccion();
const props = defineProps({
  alertas: { type: Array, default: () => [] },
  umbral: { type: Number, default: 10 },
  vista: { type: String, default: "web" }
});

const emit = defineEmits(["umbral-change", "verProducto"]);
const umbralLocal = ref(props.umbral);
const emitChange = () => emit("umbral-change", umbralLocal.value);

// Modal de lista de productos por tienda
const modalVisible = ref(false);
const modalTienda = ref("");
const modalItems = ref([]);

const formatStoreName = (name) => {
  if (!name) return "";
  return name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

const promedio = (items) => {
  const avg = items.reduce((s, i) => s + i.diferencia_pct, 0) / items.length;
  return Math.round(avg * 10) / 10;
};

const alertasFiltradas = computed(() => {
  if (!props.alertas) return [];
  return props.alertas.filter(a => a.diferencia_pct >= umbralLocal.value);
});

const agrupadoPorTienda = computed(() => {
  const grupos = {};
  for (const a of alertasFiltradas.value) {
    const k = a.competidor_mas_barato;
    if (!grupos[k]) grupos[k] = [];
    grupos[k].push(a);
  }
  return Object.fromEntries(
    Object.entries(grupos).sort((a, b) => b[1].length - a[1].length)
  );
});

const modalItemsOrdenados = computed(() =>
  [...modalItems.value].sort((a, b) => b.diferencia_pct - a.diferencia_pct)
);

const abrirModal = (tienda, items) => {
  modalTienda.value = tienda;
  modalItems.value = items;
  modalVisible.value = true;
};

const cerrarModal = () => { modalVisible.value = false; };

// Modal de detalle de producto (ahora usa componente compartido)
const detalleVisible = ref(false);
const detalleCodigo = ref("");
const detalleDescripcion = ref("");

const abrirDetalle = (codigo, descripcion) => {
  detalleCodigo.value = codigo;
  detalleDescripcion.value = descripcion;
  detalleVisible.value = true;
};

const _buildRowsPA = () => {
  const headers = ["Codigo", "Producto", "Tu Precio", "Precio Competidor", "Diferencia %", "Competidor"];
  const rows = modalItemsOrdenados.value.map(item => [
    item.codigo, item.descripcion,
    item.precio_cliente, item.precio_competidor,
    item.diferencia_pct + '%',
    formatStoreName(modalTienda.value)
  ]);
  return { headers, rows };
};

const exportarExcel = () => {
  const { headers, rows } = _buildRowsPA();
  exportarAExcel({
    headers, rows,
    priceColumns: [2, 3],
    filename: "precios_altos_" + modalTienda.value + "_" + new Date().toISOString().slice(0,10) + ".xlsx",
    sheetName: 'Precios Altos'
  });
};

const exportarCsv = () => {
  const { headers, rows } = _buildRowsPA();
  exportarACsv({
    headers, rows,
    filename: "precios_altos_" + modalTienda.value + "_" + new Date().toISOString().slice(0,10) + ".csv"
  });
};
</script>

<style scoped>
/* ==================== HEADER + CONTROLES ==================== */
.subtitulo {
  font-size: 11.5px;
  color: var(--color-text-muted);
  margin: -4px 0 14px;
  font-family: 'Inter', sans-serif;
}
.total-badge {
  font-family: var(--font-mono);
  font-size: 10.5px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  border-radius: 3px;
  padding: 2px 8px;
  margin-left: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
}
.controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 2px;
  padding: 8px 12px;
}
.controls label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}
.controls input[type="range"] {
  flex: 1;
  max-width: 200px;
  accent-color: var(--color-accent);
}
.umbral-value {
  font-weight: 700;
  color: var(--color-accent);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  min-width: 46px;
  text-align: right;
}

/* ==================== LISTA DE TIENDAS ==================== */
.tiendas-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.tienda-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-surface-2);
  border-left: 3px solid var(--color-danger);
  border-top: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s;
}
.tienda-row:hover {
  background: var(--color-hover-row);
  border-color: var(--color-border-strong);
  transform: translateX(2px);
}
.tienda-info { display: flex; flex-direction: column; gap: 3px; }
.tienda-nombre {
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text);
}
.tienda-count {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}
.ver-btn {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  padding: 5px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10.5px;
  font-family: var(--font-mono);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.ver-btn:hover {
  background: var(--color-accent);
  color: var(--color-surface-3);
}
.no-alerts-message {
  padding: 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 12px;
  font-style: italic;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
}

/* ==================== MODAL ==================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 3px;
  width: 92%;
  max-width: 950px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.modal-header h4 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.modal-actions { display: flex; gap: 6px; }
.export-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.export-btn:hover {
  color: var(--color-success);
  border-color: var(--color-success);
}
.export-btn-csv:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
}
.close-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 11px;
  font-family: var(--font-mono);
  transition: all 0.15s;
}
.close-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.modal-body {
  overflow-y: auto;
  padding: 12px 16px 16px;
}
.modal-table {
  width: 100%;
  border-collapse: collapse;
}
.modal-table th {
  text-align: left;
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  border-bottom: 1px solid var(--color-border-strong);
  background: var(--color-surface-3);
  position: sticky;
  top: 0;
}
.modal-table td {
  padding: 7px 8px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
}
.modal-table tbody tr:hover { background: var(--color-hover-row); }
.code-cell {
  font-family: var(--font-mono);
  color: var(--color-text-faint);
  font-size: 10.5px;
}
.codigo-link {
  cursor: pointer;
  color: var(--color-accent);
  font-family: var(--font-mono);
  transition: color 0.1s;
}
.codigo-link:hover {
  color: var(--color-accent-strong);
  text-decoration: underline;
}

/* Precios altos: tu precio es ALTO (rojo), competidor BAJO (verde) */
.price-alto, .price-link-alto {
  color: var(--color-danger-text);
  font-weight: 700;
  font-family: var(--font-mono);
  text-decoration: none;
}
.price-link-alto:hover { text-decoration: underline; }

.price-bajo, .price-link-bajo {
  color: var(--color-success-text);
  font-weight: 600;
  font-family: var(--font-mono);
  text-decoration: none;
}
.price-link-bajo:hover { text-decoration: underline; }

.diff-cell, .margen-cell {
  color: var(--color-warning-text);
  font-weight: 700;
  font-family: var(--font-mono);
}

.corregir-small {
  background: transparent;
  color: var(--color-warning);
  border: 1px solid var(--color-warning-border);
  padding: 2px 6px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10px;
  margin-left: 4px;
  transition: all 0.1s;
}
.corregir-small:hover {
  background: var(--color-warning-bg);
}
</style>